import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Copy, Check, ExternalLink, ChevronRight, FolderGit2, UserCog, ShieldCheck, ListPlus, FileCog } from 'lucide-react'
import { Card } from '../components/ui/Card'
import { IdeInstallMenu } from '../components/install/IdeInstallMenu'
import { resolveMcpBaseUrl } from '../lib/mcpConfig'
import { PLATFORM_ENTRY } from '../lib/catalog'
import { IDE_REGISTRY } from '../lib/ideInstall'

function CopyBlock({ code, label }: Readonly<{ code: string; label?: string }>) {
  const [copied, setCopied] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      {label && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>
          {label}
        </div>
      )}
      <pre style={{
        background: 'var(--surface-hover)', padding: 'var(--space-2)',
        borderRadius: 'var(--radius-s)', fontSize: 12, overflow: 'auto',
        border: '1px solid var(--border)', margin: 0, lineHeight: 1.6,
      }}>
        {code}
      </pre>
      <button
        onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
        style={{
          position: 'absolute', top: label ? 28 : 8, right: 8,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
          color: 'var(--text-muted)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        {copied ? <Check size={12} color="var(--success)" /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

function StepCard({ number, title, description, children, defaultOpen = false, accent = 'var(--brand)' }: Readonly<{
  number: number; title: string; description: string; children: React.ReactNode; defaultOpen?: boolean; accent?: string
}>) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Card style={{ marginBottom: 'var(--space-2)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-2)', width: '100%',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          color: 'var(--text-primary)', textAlign: 'left',
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: accent, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700,
        }}>{number}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{description}</div>
        </div>
        <ChevronRight size={16} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: '0.15s', color: 'var(--text-muted)' }} />
      </button>
      {open && <div style={{ marginTop: 'var(--space-2)', paddingLeft: 44 }}>{children}</div>}
    </Card>
  )
}

// Section banner that separates the two onboarding phases (per-project vs per-developer).
function PhaseHeader({ icon: Icon, badge, title, subtitle, accent, id }: Readonly<{
  icon: React.ElementType; badge: string; title: string; subtitle: React.ReactNode; accent: string; id?: string
}>) {
  return (
    <div id={id} style={{
      marginTop: 'var(--space-5)', marginBottom: 'var(--space-3)',
      paddingTop: 'var(--space-3)', borderTop: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 'var(--radius-s)', flexShrink: 0,
          background: `${accent}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={16} color={accent} />
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{title}</h2>
        <span style={{
          fontSize: 11, fontWeight: 600, color: accent, background: `${accent}1f`,
          padding: '2px 10px', borderRadius: 999,
        }}>{badge}</span>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 0 40px', lineHeight: 1.6 }}>
        {subtitle}
      </p>
    </div>
  )
}

// Small numbered sub-step used inside "Register your system" to visually separate
// the three files a repo owner touches (system.yaml, index.yaml, allowed_agents.yaml).
function MiniStep({ n, icon: Icon, accent, title, filePath, children }: Readonly<{
  n: number; icon: React.ElementType; accent: string; title: string; filePath: string; children: React.ReactNode
}>) {
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 'var(--radius-s)',
      padding: 'var(--space-2) var(--space-3)', marginBottom: 'var(--space-2)',
      background: 'var(--surface)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{
          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
          background: accent, color: '#fff', fontSize: 11, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{n}</div>
        <Icon size={14} color={accent} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        <code style={{
          fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface-hover)',
          padding: '1px 6px', borderRadius: 4,
        }}>{filePath}</code>
      </div>
      <div style={{ paddingLeft: 28 }}>{children}</div>
    </div>
  )
}

// ─── Repo URL → Project ID helper ────────────────────────────────────────────

function extractProjectId(repoUrl: string): string | null {
  const trimmed = repoUrl.trim()
  if (!trimmed) return null
  // Strip trailing .git if present
  const clean = trimmed.replace(/\.git$/, '')
  // Extract last path segment (works for SSH git@host:org/repo and HTTPS https://host/org/repo)
  const match = /[/:]([^/:]+)$/.exec(clean)
  return match ? match[1] : null
}

function RepoUrlHelper() {
  const [repoUrl, setRepoUrl] = useState('')
  const [projectId, setProjectId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  function handleExtract() {
    const id = extractProjectId(repoUrl)
    setProjectId(id)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleExtract()
  }

  function copyId() {
    if (projectId) {
      navigator.clipboard.writeText(projectId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div style={{
      marginBottom: 'var(--space-3)', padding: 'var(--space-3)',
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-s)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <FolderGit2 size={14} color="var(--brand)" />
        What's my project ID?
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.6 }}>
        Paste your repository URL below to get the folder name / project ID you should use.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <input
          type="text"
          value={repoUrl}
          onChange={e => { setRepoUrl(e.target.value); setProjectId(null) }}
          onKeyDown={handleKeyDown}
          placeholder="e.g. git@github.com:kone-rnd/elevator-controls.git"
          style={{
            flex: 1, padding: '8px 12px', fontSize: 13,
            border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)',
            background: 'var(--bg)', color: 'var(--text-primary)',
            outline: 'none',
          }}
        />
        <button
          onClick={handleExtract}
          disabled={!repoUrl.trim()}
          style={{
            padding: '8px 16px', fontSize: 13, fontWeight: 600,
            background: repoUrl.trim() ? 'var(--brand)' : 'var(--surface-hover)',
            color: repoUrl.trim() ? '#fff' : 'var(--text-muted)',
            border: 'none', borderRadius: 'var(--radius-xs)',
            cursor: repoUrl.trim() ? 'pointer' : 'not-allowed',
            whiteSpace: 'nowrap',
          }}
        >
          Get Project ID
        </button>
      </div>
      {projectId && (
        <div style={{
          marginTop: 10, padding: '10px 14px',
          background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)',
          borderRadius: 'var(--radius-xs)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Your project ID / folder name:</div>
            <code style={{ fontSize: 15, fontWeight: 700, color: 'var(--success)' }}>{projectId}</code>
          </div>
          <button
            onClick={copyId}
            style={{
              padding: '4px 10px', fontSize: 11, fontWeight: 600,
              background: copied ? 'var(--success)' : 'var(--surface)',
              color: copied ? '#fff' : 'var(--text-secondary)',
              border: '1px solid var(--border)', borderRadius: 4,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
          </button>
        </div>
      )}
      {projectId === null && repoUrl.trim() !== '' && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
          Press "Get Project ID" or Enter to extract the ID from your URL.
        </div>
      )}
    </div>
  )
}

const PERSONAL_TOOLS: { id: string; label: string; instructions: string; code: string; note: string }[] = [
  {
    id: 'vscode',
    label: 'VS Code',
    instructions: 'Open VS Code → Ctrl+Shift+P → "MCP: Open User Configuration" → add the servers below.',
    code: `"mcp": {
  "servers": {
    "mcp-atlassian": {
      "command": "uvx",
      "args": ["mcp-atlassian"],
      "env": {
        "JIRA_URL": "https://kone.atlassian.net",
        "JIRA_USERNAME": "your.name@kone.com",
        "JIRA_API_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>",
        "CONFLUENCE_URL": "https://kone.atlassian.net/wiki",
        "CONFLUENCE_USERNAME": "your.name@kone.com",
        "CONFLUENCE_API_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>"
      }
    },
    "mcp-github": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-github"],
      "env": {
        "GITHUB_PERSONAL_ACCESS_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>"
      }
    }
  }
}`,
    note: 'This goes in VS Code MCP User Config (NOT in .vscode/ inside any repo). The same Atlassian token works for both Jira and Confluence.',
  },
  {
    id: 'kiro',
    label: 'Kiro',
    instructions: 'Edit your user-level MCP config at C:\\Users\\<you>\\.kiro\\settings\\mcp.json:',
    code: `{
  "mcpServers": {
    "mcp-atlassian": {
      "command": "uvx",
      "args": ["mcp-atlassian"],
      "env": {
        "JIRA_URL": "https://kone.atlassian.net",
        "JIRA_USERNAME": "your.name@kone.com",
        "JIRA_API_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>",
        "CONFLUENCE_URL": "https://kone.atlassian.net/wiki",
        "CONFLUENCE_USERNAME": "your.name@kone.com",
        "CONFLUENCE_API_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>"
      }
    },
    "mcp-github": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-github"],
      "env": {
        "GITHUB_PERSONAL_ACCESS_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>"
      }
    }
  }
}`,
    note: 'For Kiro, paste your actual token values directly into the file. This file is at user-level (C:\\Users\\<you>\\.kiro\\settings\\mcp.json) — never inside any repo, never committed. Kiro does NOT resolve environment variables from your shell.',
  },
  {
    id: 'cursor',
    label: 'Cursor',
    instructions: 'Edit your user-level MCP config at C:\\Users\\<you>\\.cursor\\mcp.json:',
    code: `{
  "mcpServers": {
    "mcp-atlassian": {
      "command": "uvx",
      "args": ["mcp-atlassian"],
      "env": {
        "JIRA_URL": "https://kone.atlassian.net",
        "JIRA_USERNAME": "your.name@kone.com",
        "JIRA_API_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>",
        "CONFLUENCE_URL": "https://kone.atlassian.net/wiki",
        "CONFLUENCE_USERNAME": "your.name@kone.com",
        "CONFLUENCE_API_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>"
      }
    },
    "mcp-github": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-github"],
      "env": {
        "GITHUB_PERSONAL_ACCESS_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>"
      }
    }
  }
}`,
    note: 'Paste  your actual token values directly into the file. This file is at user-level (C:\\Users\\<you>\\.cursor\\mcp.json) — never inside any repo, never committed.',
  },
  {
    id: 'claude',
    label: 'Claude Code',
    instructions: 'Edit your user-level MCP config at C:\\Users\\<you>\\.claude\\settings.json:',
    code: `{
  "mcpServers": {
    "mcp-atlassian": {
      "command": "uvx",
      "args": ["mcp-atlassian"],
      "env": {
        "JIRA_URL": "https://kone.atlassian.net",
        "JIRA_USERNAME": "your.name@kone.com",
        "JIRA_API_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>",
        "CONFLUENCE_URL": "https://kone.atlassian.net/wiki",
        "CONFLUENCE_USERNAME": "your.name@kone.com",
        "CONFLUENCE_API_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>"
      }
    },
    "mcp-github": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-github"],
      "env": {
        "GITHUB_PERSONAL_ACCESS_TOKEN": "<REPLACE_WITH_YOUR_TOKEN>"
      }
    }
  }
}`,
    note: 'Paste your actual token values directly into the file. This file is at user-level (C:\\Users\\<you>\\.claude\\settings.json) — never inside any repo, never committed.',
  },
]

function PersonalToolsTabs() {
  const [active, setActive] = useState('vscode')
  const [userEmail, setUserEmail] = useState('your.name@kone.com')

  // Fetch logged-in user's email
  useEffect(() => {
    const BASE = import.meta.env.VITE_API_URL ?? ''
    fetch(`${BASE}/api/v1/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(u => { if (u?.email) setUserEmail(u.email) })
      .catch(() => {})
  }, [])

  const tool = PERSONAL_TOOLS.find(t => t.id === active)!
  const codeWithEmail = tool.code.replace(/your\.name@kone\.com/g, userEmail)
  return (
    <div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 'var(--space-2)' }}>
        {PERSONAL_TOOLS.map(t => (
          <button
            key={t.id}
            onClick={() => setActive(t.id)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none', borderBottom: active === t.id ? '2px solid var(--brand)' : '2px solid transparent',
              color: active === t.id ? 'var(--brand)' : 'var(--text-muted)',
            }}
          >{t.label}</button>
        ))}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 'var(--space-2)' }}>
        {tool.instructions}
      </div>
      <div style={{ marginBottom: 'var(--space-1)', padding: '6px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-s)', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
        {'⚠ After pasting, replace <REPLACE_WITH_YOUR_TOKEN> with your actual tokens before using.'}
      </div>
      <CopyBlock label={active === 'vscode' ? 'VS Code MCP User Config — NEVER commit this' : '.kiro/settings/mcp.json (user-level) — NEVER commit this'} code={codeWithEmail} />
      <div style={{ marginTop: 'var(--space-2)', fontSize: 12, color: 'var(--text-muted)' }}>
        <strong>Note:</strong> {tool.note}
      </div>
      <div style={{ marginTop: 'var(--space-2)', padding: '8px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)' }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Where to get tokens:</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7 }}>
          • <strong>Jira/Confluence:</strong> <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer" style={{ color: 'var(--brand)' }}>id.atlassian.com/manage-profile/security/api-tokens</a> (same token for both)<br />
          • <strong>GitHub:</strong> Settings → Developer Settings → Personal Access Tokens → <strong>Tokens (classic)</strong><br />
          &nbsp;&nbsp;&nbsp;Select scopes: <code>repo</code>, <code>read:org</code>, <code>read:user</code>, <code>workflow</code><br />
          &nbsp;&nbsp;&nbsp;After creating, click <strong>"Configure SSO" → "Authorize"</strong> for your KONE org before using
        </div>
      </div>
    </div>
  )
}

const PROJECT_ACCENT = 'var(--warning)'
const USER_ACCENT = 'var(--brand)'

export function QuickStart() {
  const location = useLocation()

  // Scroll to hash anchor on navigation (React Router doesn't do this automatically)
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }, [location.hash])

  return (
    <div style={{ padding: 'var(--space-4)', maxWidth: 820, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
          Quick Start Guide
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
          Get AI assistance in your IDE. Pick your path:
        </p>
      </div>

      {/* Pick your path — compact inline buttons, not cards */}
      <div style={{
        display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)',
        padding: 'var(--space-2)', background: 'var(--surface)', borderRadius: 'var(--radius-s)',
        border: '1px solid var(--border)',
      }}>
        <a
          href="#your-setup"
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 14px', borderRadius: 'var(--radius-xs)',
            background: 'rgba(25,100,250,0.1)', border: '1px solid var(--brand)',
            textDecoration: 'none', color: 'inherit', cursor: 'pointer',
          }}
        >
          <UserCog size={16} color="var(--brand)" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand)' }}>Developer — set up my IDE →</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Get your token &amp; personal tools · ~2 min</div>
          </div>
        </a>
        <a
          href="#project-onboarding"
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 14px', borderRadius: 'var(--radius-xs)',
            background: 'transparent', border: '1px solid var(--border)',
            textDecoration: 'none', color: 'inherit', cursor: 'pointer',
          }}
        >
          <FolderGit2 size={16} color={PROJECT_ACCENT} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Tech Lead — onboard a project →</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Register &amp; connect a new repo · ~5 min</div>
          </div>
        </a>
      </div>

      {/* Prerequisites — compact single line */}
      <Card style={{ marginBottom: 'var(--space-2)' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Prerequisites:</strong> VS Code 1.99+ (Copilot), Kiro, Cursor, or Claude Code · Python 3.10+ for <code>uvx</code> (Jira/GitLab tools) · access to your team's Git repo.
        </div>
      </Card>

      {/* ─────────────── PHASE 1: PER-PROJECT (one-time) ─────────────── */}
      <PhaseHeader
        icon={FolderGit2}
        accent={PROJECT_ACCENT}
        badge="One-time · tech lead"
        title="Project onboarding"
        id="project-onboarding"
        subtitle={<>Done <strong>once per repository</strong> by a tech lead. After these 3 steps, every developer on the team just needs the <a href="#your-setup" style={{ color: 'var(--brand)' }}>personal setup</a>.</>}
      />

      <StepCard
        number={1}
        accent={PROJECT_ACCENT}
        title="Register your project (via Git MR)"
        description="3 small file changes in the canonical repo — takes about 5 minutes."
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
          background: 'var(--surface)', borderRadius: 'var(--radius-s)',
          border: '1px solid var(--border)', marginBottom: 'var(--space-2)',
        }}>
          <FolderGit2 size={14} color="var(--brand)" />
          <div style={{ flex: 1, fontSize: 12 }}>
            <strong>Canonical repo:</strong>{' '}
            <a href="https://github.com/kone-rnd/digital-innovation-sdlc-ai-baseline" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)' }}>
              kone-rnd/digital-innovation-sdlc-ai-baseline
            </a>
            <span style={{ color: 'var(--text-muted)' }}> → <code>sdlc-canonical/</code> folder</span>
          </div>
          <a
            href="https://github.com/kone-rnd/digital-innovation-sdlc-ai-baseline/tree/main/sdlc-canonical"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '4px 10px', background: 'var(--brand)', color: '#fff',
              borderRadius: 'var(--radius-xs)', fontSize: 11, fontWeight: 600,
              textDecoration: 'none', whiteSpace: 'nowrap',
            }}
          >
            Open repo ↗
          </a>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 'var(--space-3)' }}>
          All three changes below go into the canonical repo above. Create a branch, make the changes, and open one merge request.
        </div>

        <RepoUrlHelper />

        <MiniStep n={1} icon={FileCog} accent={PROJECT_ACCENT} title="Declare your project" filePath="sdlc-canonical/systems/<your-project-id>/system.yaml">
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 8 }}>
            Create a folder using your <strong>repo name</strong> (the last part of your git URL) as the project ID. This is the same ID every IDE uses to fetch your AI config.
          </div>
          <div style={{ padding: '8px 10px', background: 'rgba(25,100,250,0.06)', borderRadius: 'var(--radius-xs)', border: '1px solid var(--brand)', marginBottom: 10, fontSize: 12 }}>
            <strong>How to pick your project ID:</strong> take the last part of your git remote URL.<br />
            <code>git@github.com:kone-rnd/<strong>elevator-controls</strong>.git</code> → project ID = <code>elevator-controls</code>
          </div>
          <CopyBlock
            label="system.yaml — replace every value below"
            code={`id: elevator-controls            # ← repo name from your git URL (the project ID)
name: "Elevator Controls"        # ← human-readable name shown in the UI
team: fleet-engineering          # ← your team name (used for grouping)
repos:
  - kone-rnd/elevator-controls   # ← org/repo from your git URL
profile: python-service           # ← determines which rules apply
                                  #   options: python-service, nodejs-service,
                                  #   java-spring, react-frontend, typescript-service
agents:
  - repo-navigator               # ← agents to deliver (browse Catalog for IDs)
skills:
  - repo-exploration             # ← skills to deliver (browse Catalog for IDs)`}
          />
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            <strong>id</strong> — must equal the repo name (last segment of your git URL). Every IDE calls <code>get_startup_context</code> with this to fetch config.<br />
            <strong>repos</strong> — full <code>org/repo</code> slug. The platform matches this against <code>git remote get-url origin</code> to identify your project.<br />
            <strong>profile</strong> — controls which rules apply. Check the <a href="/catalog?tab=playground" style={{ color: 'var(--brand)' }}>Playground</a> to preview what a profile delivers.<br />
            <strong>agents / skills</strong> — browse the <a href="/catalog" style={{ color: 'var(--brand)' }}>Catalog</a> to see available IDs.
          </div>
          <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--surface)', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border)', fontSize: 12, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>Using existing agents/skills (most common):</strong>
            Browse the <a href="/catalog" style={{ color: 'var(--brand)' }}>Catalog</a> → copy the ID of an agent or skill you want → add it to your <code>agents:</code> or <code>skills:</code> list above. That's it — no new files needed.

            <strong style={{ color: 'var(--text-primary)', display: 'block', marginTop: 10, marginBottom: 4 }}>Creating a brand-new agent (only if it doesn't exist yet):</strong>
            <ol style={{ margin: 0, paddingLeft: 16 }}>
              <li>Create the agent folder: <code>sdlc-canonical/agents/&lt;agent-id&gt;/</code> with <code>manifest.yaml</code> + <code>persona.md</code></li>
              <li>Register it in <code>sdlc-canonical/index.yaml</code> under <code>agents:</code></li>
              <li>Add the agent ID to your project's <code>system.yaml</code> → <code>agents:</code></li>
              <li>If you have <code>allowed_agents.yaml</code>, add the ID there too</li>
            </ol>
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
              All changes go in <strong>one MR</strong> to the canonical repo. Once merged, available to every IDE automatically.
              Use an existing agent as a template: <a href="https://github.com/kone-rnd/digital-innovation-sdlc-ai-baseline/tree/main/sdlc-canonical/agents/repo-navigator" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)' }}>repo-navigator example ↗</a>
            </div>
          </div>
        </MiniStep>

        <MiniStep n={2} icon={ListPlus} accent={PROJECT_ACCENT} title="Register the project in the master index" filePath="sdlc-canonical/index.yaml">
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 8 }}>
            The platform only loads projects listed here — add an entry under the <code>systems:</code> section pointing at the folder from step 1.
          </div>
          <CopyBlock
            label="index.yaml — add this block under systems:"
            code={`  - id: elevator-controls        # ← must match the id in system.yaml
    type: system
    path: systems/elevator-controls  # ← folder path relative to sdlc-canonical/`}
          />
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
            <code>id</code> and <code>path</code> must match the folder name and <code>id</code> from your <code>system.yaml</code>.
          </div>
        </MiniStep>

        <MiniStep n={3} icon={ShieldCheck} accent={PROJECT_ACCENT} title="Restrict which agents can be invoked (optional but recommended)" filePath="sdlc-canonical/access-control/<org>/<repo>/allowed_agents.yaml">
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 8 }}>
            Without this file, <em>any</em> agent declared for your profile can be invoked in your repo. Add this list to lock it down to only the agents you trust — the path is your repo slug from <code>system.yaml</code> (<code>org/repo</code>), split into folders.
          </div>
          <CopyBlock
            label="allowed_agents.yaml"
            code={`# Agent access control for kone-rnd/elevator-controls
#   ↑ replace with your org/repo from system.yaml
# Only these agents can be invoked in this repo:
- repo-navigator               # ← list one agent ID per line`}
          />
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            List only the agent IDs this repo is allowed to invoke — one per line. Any agent not on this list is rejected at the MCP layer with a clear denial message, even if it's declared in <code>system.yaml</code>.<br />
            <strong>Tip:</strong> for a decentralized alternative, commit the same list as <code>.sdlc/allowed_agents.yaml</code> at the root of your own repo instead — the MCP server checks that path first when it can read your filesystem.
          </div>
        </MiniStep>

        <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)', fontSize: 12 }}>
          <strong>Then:</strong> commit all three changes on one branch, open a merge request to the canonical repository, and merge. The platform then delivers matched rules + your declared/allowed agents to any developer who opens a matching repo in their IDE. Use the <strong>Playground</strong> page to preview what gets delivered for your profile.
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
          Note: agents, skills, rules, and profiles are actively maintained and may change. Always check the canonical repo or the <strong>Catalog</strong> page for the current available set.
        </div>
      </StepCard>

      <StepCard
        number={2}
        accent={PROJECT_ACCENT}
        title="Add MCP config to your repository"
        description="One file per IDE, committed once. Connects every developer's IDE to the platform."
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 'var(--space-2)' }}>
          Choose the config for your IDE. Create the file at the path shown and commit it.
        </div>
        <IdeInstallMenu
          entry={PLATFORM_ENTRY}
          defaultIde="vscode"
          baseUrl={resolveMcpBaseUrl()}
          ides={[
            ...IDE_REGISTRY.filter(i => i.id === 'vscode'),
            ...IDE_REGISTRY.filter(i => i.id === 'kiro'),
            ...IDE_REGISTRY.filter(i => i.id !== 'vscode' && i.id !== 'kiro'),
          ]}
        />
      </StepCard>

      <StepCard
        number={3}
        accent={PROJECT_ACCENT}
        title="Add AGENTS.md to your repository"
        description="Tells the AI what project this is. Commit to repo root."
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 'var(--space-2)' }}>
          Create <code>AGENTS.md</code> at the <strong>root</strong> of your own repository (not the canonical repo). Replace the project ID:
        </div>
        <CopyBlock
          label="AGENTS.md — commit to your repo root"
          code={`---\nproject: elevator-controls  # ← same id as system.yaml\n---\n# Elevator Controls\n\nThese steps are mandatory. Do them BEFORE composing any response.\n\n### Step 1 — Bootstrap (once per session)\n\nAt the start of every conversation, **always** call the sdlc-platform MCP:\n\n1. Run \`git remote get-url origin\` to obtain \`repo_remote\`.\n2. Call \`get_startup_context(repo_path=<absolute path to this repository>, repo_remote=<git remote get-url origin output>)\` as your **VERY FIRST** tool call — before reading any files, before responding to the user.\n- \`repo_remote\` is required. Pass \`""\` only if the repo has no remote or the command fails; never omit it.\n- Read \`session_id\` from the response and reuse it in **every** subsequent MCP call.\n- Read \`governing_rules.mandatory\` and apply every rule immediately.\n\n> **Note on IDE wiring files** (\`.kiro/agents/kone-context-lake.md\`, \`.github/agents/kone-context-lake.agent.md\`, \`.claude/agents/kone-context-lake.md\`): these are lightweight routing configs, **not** a substitute for calling the MCP. Their presence means the repo is set up correctly — you still MUST call \`get_startup_context\` on every session start.\n\n### Step 2 — Create missing IDE wiring files\n\nAfter \`get_startup_context\` returns, check \`setup.ide_files\` in the response:\n\n- For each file listed: if it does **not** exist at the given path → create it with the provided content.\n- If it already exists → skip (never overwrite).\n- Create files for ALL IDEs listed, not just the current one.\n- Delete any stale files listed in \`setup.instruction\`.\n\n### Step 3 — Get context before every response\n\nFor **every** user message (not just the first), before composing a response:\n\n1. Call \`get_context(session_id, task=<user's message>)\`.\n2. Read \`required_next_step\` from the response and call the indicated agent (e.g. \`get_agent(agent_id, session_id)\`).\n3. Apply the agent persona and follow its output format.\n4. Only then compose and send your response.`}
        />
        <div style={{ marginTop: 'var(--space-2)', padding: '8px 12px', background: 'var(--surface)', borderRadius: 'var(--radius-s)', border: '1px solid var(--border)', fontSize: 12 }}>
          <strong>Tip:</strong> After your canonical repo MR merges, your project appears in <a href="/projects" style={{ color: 'var(--brand)' }}>My Projects</a>. Open it to get a <strong>pre-filled copy</strong> of AGENTS.md with your exact project ID — no editing needed.
        </div>
      </StepCard>

      {/* ─────────────── PHASE 2: PER-DEVELOPER ─────────────── */}
      <PhaseHeader
        icon={UserCog}
        accent={USER_ACCENT}
        badge="Per developer · ~2 min"
        title="Your setup"
        subtitle="What every developer does on their own machine."
        id="your-setup"
      />

      {/* Pre-check for developers */}
      <Card style={{ marginBottom: 'var(--space-2)', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Before you start — quick check</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          Open your repository root and verify these files exist:
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
            <li><Check size={12} color="var(--success)" style={{ verticalAlign: 'middle' }} /> <code>AGENTS.md</code> — should contain your project ID in the frontmatter</li>
            <li><Check size={12} color="var(--success)" style={{ verticalAlign: 'middle' }} /> <code>.vscode/mcp.json</code> or <code>.kiro/settings/mcp.json</code> — MCP server config</li>
          </ul>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
            If either file is missing, ask your tech lead to complete the <a href="#project-onboarding" style={{ color: 'var(--brand)' }}>project onboarding</a> first.
          </div>
        </div>
      </Card>

      <div id="get-platform-token" />
      <StepCard
        number={1}
        accent={USER_ACCENT}
        defaultOpen
        title="Get your platform token (Azure AD login)"
        description="Authenticate with your KONE Azure AD account to obtain an MCP bearer token."
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            <li>Open <a href="https://sdlc-mcp.dev.wenni.io" target="_blank" rel="noreferrer" style={{ color: 'var(--brand)' }}>sdlc-mcp.dev.wenni.io</a> in your browser</li>
            <li>Click <strong>Login</strong> — you'll be redirected to Azure AD (KONE SSO)</li>
            <li>Sign in with your <code>@kone.com</code> credentials (same as Teams/Outlook)</li>
            <li>After login, go to <strong>Profile</strong> page</li>
            <li>Click <strong>Copy Token</strong> — this is your MCP bearer token</li>
          </ol>
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)', fontSize: 12 }}>
            <strong>How it works:</strong> The platform authenticates you via Azure AD (through AWS Cognito federation), then issues a platform JWT. This JWT is what your IDE sends to the MCP server on every request. Tokens expire — revisit the Profile page to get a fresh one when needed.
          </div>
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)', fontSize: 12 }}>
            <strong>VS Code users:</strong> You don't need to manually paste the token every time. The MCP config uses <code>$&#123;input:sdlcToken&#125;</code> which prompts you once and stores it securely in VS Code's credential store. Just paste it when prompted.
          </div>
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)', fontSize: 12 }}>
            <strong>Kiro / Cursor / Claude Code users:</strong> Your repo's <code>.kiro/settings/mcp.json</code> (or equivalent) references <code>$&#123;SDLC_TOKEN&#125;</code>. Set this environment variable in your shell before launching the IDE:
            <pre style={{ margin: '8px 0 0', padding: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, lineHeight: 1.6 }}>
              {`# Windows (PowerShell)
$env:SDLC_TOKEN="paste-your-token-here"

# macOS / Linux
export SDLC_TOKEN="paste-your-token-here"`}
            </pre>
            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
              Or, if your workspace config uses a direct value (no <code>$&#123;SDLC_TOKEN&#125;</code> variable), paste the token directly into the <code>Authorization</code> header in your <code>.kiro/settings/mcp.json</code>.
              <br /><br />
              <strong>⚠ Restart/reload your IDE after setting the environment variable</strong> — the IDE only reads env vars at launch time.
            </div>
          </div>
        </div>
      </StepCard>

      <StepCard
        number={2}
        accent={USER_ACCENT}
        defaultOpen
        title="Add MCP config to your repo"
        description="Create the config file for your IDE. Commit it so teammates get it automatically."
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 'var(--space-2)' }}>
          Choose your IDE below. Create the file at the path shown and commit it to the repo.
        </div>
        <IdeInstallMenu entry={PLATFORM_ENTRY} defaultIde="vscode" baseUrl={resolveMcpBaseUrl()} />
        <div style={{ marginTop: 'var(--space-3)', padding: '10px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Summary</div>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>IDE</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Config file</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Token method</th>
            </tr></thead>
            <tbody>
              <tr><td style={{ padding: '4px 6px' }}>VS Code</td><td style={{ padding: '4px 6px' }}><code>.vscode/mcp.json</code></td><td style={{ padding: '4px 6px' }}>Prompted by IDE, stored in credential store</td></tr>
              <tr><td style={{ padding: '4px 6px' }}>Kiro</td><td style={{ padding: '4px 6px' }}><code>.kiro/settings/mcp.json</code></td><td style={{ padding: '4px 6px' }}><code>SDLC_TOKEN</code> env var</td></tr>
              <tr><td style={{ padding: '4px 6px' }}>Cursor</td><td style={{ padding: '4px 6px' }}><code>.cursor/mcp.json</code></td><td style={{ padding: '4px 6px' }}><code>SDLC_TOKEN</code> env var</td></tr>
              <tr><td style={{ padding: '4px 6px' }}>Claude Code</td><td style={{ padding: '4px 6px' }}><code>.mcp.json</code></td><td style={{ padding: '4px 6px' }}><code>SDLC_TOKEN</code> env var</td></tr>
            </tbody>
          </table>
        </div>
      </StepCard>

      <StepCard
        number={3}
        accent={USER_ACCENT}
        defaultOpen
        title="Test it — open your repo and chat"
        description="Verify everything works end to end."
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            <li>Open your repo in your IDE (VS Code, Kiro, Cursor, or Claude Code)</li>
            <li>Wait 5–10 seconds — the IDE reads the MCP config and connects to the platform</li>
            <li>Open the AI chat panel:
              <ul style={{ margin: '4px 0', paddingLeft: 16, fontSize: 12 }}>
                <li><strong>VS Code:</strong> <code>Ctrl+Shift+I</code> (Copilot Chat)</li>
                <li><strong>Kiro:</strong> Chat panel opens automatically</li>
              </ul>
            </li>
            <li>Type: <em>"Explain this application"</em></li>
            <li>The AI should call <code>get_startup_context(repo_path, repo_remote)</code> automatically — look for the tool call in the chat</li>
            <li>You should see your project's rules and profile mentioned in the response</li>
          </ol>
        </div>

        <div style={{ marginTop: 'var(--space-2)', padding: '10px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>What happens behind the scenes on first invoke:</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            1. IDE reads <code>.mcp.json</code> → connects to the platform MCP server<br />
            2. AI runs <code>git remote get-url origin</code> and calls <code>get_startup_context(repo_path, repo_remote)</code> → platform identifies your project by the supplied git remote URL<br />
            3. Platform loads your project's profile → returns matched rules + declared agents + skills<br />
            4. AI is now governed by your org standards — every response follows the rules<br />
            <br />
            <strong>If you see "project: unknown":</strong> your git remote URL doesn't match any <code>repos</code> entry in <code>systems/*/system.yaml</code>. Check your project registration in the canonical repo.
          </div>
        </div>

        <div style={{ marginTop: 'var(--space-2)', padding: '10px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Test Jira integration:</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Type: <em>"Get details of IDP-123"</em> (use YOUR Jira project key + real ticket number)<br />
            → IDE prompts for your Jira token (first time only)<br />
            → AI fetches the ticket and shows summary, description, acceptance criteria
          </div>
        </div>

        <div style={{ marginTop: 'var(--space-2)', padding: '10px 12px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Test the full flow:</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Type: <em>"Implement IDP-123"</em><br />
            → AI fetches ticket → loads org rules → writes spec → generates code with tests<br />
            All following your organization's coding standards automatically.
          </div>
        </div>
      </StepCard>

      <StepCard
        number={4}
        accent={USER_ACCENT}
        defaultOpen={false}
        title="(Optional) Add personal tools — Jira, Confluence, GitHub"
        description="Uses your own token, never stored in the repo."
      >
        <PersonalToolsTabs />
      </StepCard>

      {/* Troubleshooting */}
      <Card style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-3)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 'var(--space-2)' }}>Troubleshooting</div>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)', fontWeight: 600 }}>Symptom</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)', fontWeight: 600 }}>Fix</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>MCP not connecting</td>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Check the MCP output/log in your IDE. Restart the IDE. Verify your MCP config file is in the correct location for your IDE.</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Project shows as "unknown"</td>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Your repo's git remote URL doesn't match any <code>repos</code> entry in <code>systems/*/system.yaml</code>. Check your project registration in the canonical repo.</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Jira tool not found</td>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Run <code>pip install uv</code> first (uvx needs it). Then restart your IDE.</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Token not working (VS Code)</td>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Ensure <code>"inputs"</code> array is in your config. Check JSON syntax. Try <code>Ctrl+Shift+P → MCP: Reset Trust</code>.</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Token not working (Kiro/Cursor)</td>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Verify the <code>SDLC_TOKEN</code> environment variable is set in your shell: <code>echo $SDLC_TOKEN</code>. Relaunch IDE after setting it.</td>
            </tr>
            <tr>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>Agent doesn't call get_startup_context()</td>
              <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>The MCP server needs to be connected (green status). Try restarting the IDE or toggling the MCP server off/on.</td>
            </tr>
          </tbody>
        </table>
      </Card>

      {/* Architecture note */}
      <Card>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>How the platform delivers to your project</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7 }}>
          <p style={{ margin: '0 0 8px' }}>
            When a developer chats in the IDE, the AI calls <code>get_startup_context(repo_path, repo_remote)</code>. It obtains <code>repo_remote</code> from <code>git remote get-url origin</code>; the argument is required, and an empty string is allowed only when the repository has no remote or retrieving it fails. The platform identifies the project and delivers <strong>only what's assigned</strong>:
          </p>
          <ol style={{ margin: '0 0 12px', paddingLeft: 18 }}>
            <li><strong>Project identification</strong> — matches the supplied git remote URL against the <code>repos</code> field in <code>system.yaml</code></li>
            <li><strong>Rules</strong> — filtered by your project's profile (e.g. python-service → Python rules)</li>
            <li><strong>Agents</strong> — only agents declared in your <code>system.yaml</code>, further restricted by <code>allowed_agents.yaml</code> if present</li>
            <li><strong>Skills</strong> — skills declared in your <code>system.yaml</code> for use by your declared agents</li>
          </ol>
          <div style={{ padding: '6px 10px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)', marginBottom: 12 }}>
            <strong>How it works:</strong> Each project declares which profile, agents, and skills it uses. When a developer opens the repo, only the rules matching that profile, the declared agents, and the skills declared in <code>system.yaml</code> for those agents are delivered. Agents not listed in the project's config are never available. If the repo also has an <code>allowed_agents.yaml</code> (in <code>access-control/&lt;org&gt;/&lt;repo&gt;/</code> or <code>.sdlc/</code> at the repo root), it's checked as a second, stricter gate — an agent must pass <em>both</em> checks to be invoked. Use the <strong>Playground</strong> tab to preview exactly which agents are blocked and why for any registered project.
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Platform MCP (remote)</div>
              <ul style={{ margin: 0, paddingLeft: 14 }}>
                <li>Delivers rules, agents, skills per project config</li>
                <li>Identifies the project from the git remote supplied as <code>repo_remote</code></li>
                <li>Enforces: only agents declared in <code>system.yaml</code> AND allowed by <code>allowed_agents.yaml</code> (when present) can be invoked</li>
              </ul>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>User-local MCPs (your machine)</div>
              <ul style={{ margin: 0, paddingLeft: 14 }}>
                <li>Jira, GitLab, Confluence access</li>
                <li>Uses YOUR personal tokens only</li>
                <li>Configured per-IDE (never committed to repo)</li>
              </ul>
            </div>
          </div>
          <div style={{ marginTop: 12, padding: '6px 10px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-s)' }}>
            <strong>To add or change agents/skills:</strong> Update your project's declaration in the canonical repo and open a merge request. To restrict which of those agents can actually be invoked in a specific repo, add or edit that repo's <code>allowed_agents.yaml</code> in the same MR. Changes appear in every team member's IDE on next session — no install needed. See the <a href="https://kone.atlassian.net/wiki/x/GYAPb" target="_blank" rel="noreferrer" style={{ color: 'var(--brand)' }}>Platform Architecture</a> for the full component design.
          </div>
        </div>
      </Card>

      {/* External links */}
      <div style={{ marginTop: 'var(--space-3)', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <a
          href="https://github.com/kone-rnd/digital-innovation-sdlc-ai-baseline/blob/main/docs/developer-guide.md"
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 13, color: 'var(--brand)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <ExternalLink size={12} /> Developer Guide
        </a>
        <a
          href="https://github.com/kone-rnd/digital-innovation-sdlc-ai-baseline/blob/main/docs/tech-lead-guide.md"
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 13, color: 'var(--brand)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <ExternalLink size={12} /> Tech Lead Guide
        </a>
        <a
          href="https://kone.atlassian.net/wiki/x/GYAPb"
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 13, color: 'var(--brand)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <ExternalLink size={12} /> Platform Architecture (Confluence)
        </a>
      </div>
    </div>
  )
}
